import { useLocalStorage } from '@uidotdev/usehooks';
import isEqual from 'lodash/isEqual';
import { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    useCreateListViewMutation,
    useDeleteListViewMutation,
    useSetDefaultListViewMutation,
    useUpdateListViewMutation,
} from 'store/api/listViews';
import { APIUserSlice } from 'store/api/user';
import { isTourMode } from 'tour/tourMode';
import { listViewPath, ListView, ListViewPerson, ListViewState, ListViewVisibility } from 'types/listViews';
import { captureListViewSaved } from 'utils/listAnalytics';
import { Pin, usePins } from 'utils/pins';
import {
    CLASSIC_COLUMN_STORAGE,
    ColumnLayoutStorage,
    viewColumnConfigId,
} from '../columnLayout/useColumnLayoutPersistence';
import { ColumnLayoutEntry, ColumnLayoutStore } from '../columnLayout/types';
import type { FilterConfig } from '../ListTableTypes';
import { sameViewQuery, splitViewQuery, viewLocation, viewQueryOf, withSavedView, withViewQuery } from './builtInViews';
import { configHoldsClause } from './filterChips';
import { ListViewsScopeContext } from './ListViewsScope';

/** The Save, Share and Rename dialog, while it is open. */
export type ViewDialog =
    | {
          mode: 'create';
          titleId: string;
          name: string;
          baseViewKey: string;
          state: ListViewState;
          /** The view is saved from the list as it stands, which the dialog can then describe. */
          describesList?: boolean;
      }
    | { mode: 'share' | 'rename'; view: ListView };

export type ViewDialogValues = {
    name: string;
    visibility: ListViewVisibility;
    shared: ListViewPerson[];
    pin: boolean;
    openOn: boolean;
};

type Args = {
    /**
     * The `qf` / `mf` the list opens a built-in view with: what "changed" compares a built-in view to, unless the
     * list opened on that view itself and so wrote them as it spells them.
     */
    baselineSearch: string | undefined;
    columnConfigId: string | undefined;
    /** What the list's usage events call it. */
    surface?: string;
    /** The list has written its state to the URL; until then the URL says nothing about a change. */
    ready: boolean;
    filterConfig: FilterConfig | undefined;
};

const stateOf = (query: string, columns: ColumnLayoutEntry | undefined): ListViewState => ({
    v: 1,
    query,
    ...(columns ? { columns } : {}),
});

/**
 * A list's views in the new design: which one is open, whether the list has moved off it, and what the views
 * row, the filter row's Reset and the save dialog do. Undefined outside a {@link ListViewsScopeContext}.
 */
export function useListViews({ baselineSearch, columnConfigId, surface, ready, filterConfig }: Args) {
    const scope = useContext(ListViewsScopeContext);
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const { owner } = APIUserSlice.endpoints.getUserData.useQueryState(undefined, {
        selectFromResult: ({ data }) => ({
            owner: data?.tenants?.[0] ? `${data.tenants[0]}.${data.id}` : undefined,
        }),
    });
    const storageKey = `crespect.list-columns.${owner ?? 'none'}`;
    const [columnStore, setColumnStore] = useLocalStorage<ColumnLayoutStore | undefined>(storageKey, undefined);
    const [classicStore] = useLocalStorage<ColumnLayoutStore | undefined>(CLASSIC_COLUMN_STORAGE.storageKey, undefined);
    // Re-parsed on every render: kept by content, so the storage below changes only when the classic layout does.
    const classicRaw = columnConfigId ? classicStore?.[columnConfigId] : undefined;
    const classicJson = classicRaw !== undefined ? JSON.stringify(classicRaw) : undefined;
    const classicColumns = useMemo(
        () => (classicJson !== undefined ? (JSON.parse(classicJson) as ColumnLayoutEntry) : undefined),
        [classicJson]
    );
    const [createView] = useCreateListViewMutation();
    const [updateView] = useUpdateListViewMutation();
    const [deleteView] = useDeleteListViewMutation();
    const [setDefaultView] = useSetDefaultListViewMutation();
    const { isPinned, togglePin, setPinned } = usePins();
    const [dialog, setDialog] = useState<ViewDialog | null>(null);
    // What the list wrote for its first fetch, when it opened on a built-in view with no filters in its URL:
    // that view's own filters, as the list spells them.
    const [openedWith] = useState(search);
    const [openedQuery, setOpenedQuery] = useState<string>();
    if (ready && openedQuery === undefined) {
        setOpenedQuery(viewQueryOf(search));
    }

    const activeView = scope?.activeView;
    const workingId = activeView && columnConfigId ? viewColumnConfigId(columnConfigId, activeView.id) : undefined;
    const storedColumns = activeView?.state.columns;
    // Until a layout is changed here, the list shows the one it has in classic; a saved view's own columns come first.
    const columnStorage = useMemo<ColumnLayoutStorage>(
        () => ({
            storageKey,
            fallbacks: columnConfigId
                ? {
                      [columnConfigId]: classicColumns,
                      ...(workingId ? { [workingId]: storedColumns ?? classicColumns } : {}),
                  }
                : undefined,
        }),
        [storageKey, columnConfigId, workingId, storedColumns, classicColumns]
    );

    const activeId = activeView ? String(activeView.id) : scope?.page.activeId;

    if (!scope || activeId === undefined) return undefined;

    const { listKey, page, data } = scope;
    const pathOf = (builtInId: string) => page.pathFor?.(builtInId) ?? pathname;
    const activeBuiltInId = activeView?.baseViewKey ?? page.activeId;

    const currentQuery = viewQueryOf(search);
    const working = workingId ? columnStore?.[workingId] : undefined;
    const currentColumns = activeView
        ? (working ?? storedColumns ?? classicColumns)
        : columnConfigId
          ? (columnStore?.[columnConfigId] ?? classicColumns)
          : undefined;
    // Column changes count on an own view only: on a built-in or a shared view they just stay in the browser.
    const columnsChanged = !!activeView?.canEdit && working !== undefined && !isEqual(working, storedColumns);
    const builtInQuery =
        viewQueryOf(openedWith) === '' && openedQuery !== undefined ? openedQuery : viewQueryOf(baselineSearch ?? '');
    // In a record's tab, the view's filters on fields the tab leaves out are the record's: not compared.
    const viewQuery = splitViewQuery(
        activeView?.state.query ?? '',
        scope.inRecord ? configHoldsClause(filterConfig) : () => true
    );
    const savedQuery = activeView ? viewQuery.kept : builtInQuery;
    const queryChanged = !sameViewQuery(currentQuery, savedQuery);
    const isChanged = ready && (queryChanged || columnsChanged);

    const forgetWorkingColumns = (viewId: number) => {
        if (!columnConfigId || isTourMode()) return;
        const id = viewColumnConfigId(columnConfigId, viewId);
        setColumnStore((previous) => {
            if (!previous?.[id]) return previous;
            const { [id]: _dropped, ...rest } = previous;
            return rest;
        });
    };

    // Opens the list's own page, even when pinned from a record's tab.
    const viewPin = (view: ListView): Pin => ({
        key: `view:${view.id}`,
        label: view.name,
        url: listViewPath(listKey, view.baseViewKey),
        view: { listKey, id: view.id },
    });

    const openBuiltIn = (viewId: string) =>
        page.onSelect
            ? page.onSelect(viewId)
            : navigate({ pathname: pathOf(viewId), search: withSavedView('', viewId) });

    const openView = (view: ListView) => {
        forgetWorkingColumns(view.id);
        navigate(viewLocation(pathOf(view.baseViewKey), view));
    };

    // Puts back only what "changed" compares, the view's filters, sort and columns: the search text and the params
    // the page owns, such as the portfolio's scope, stay as they are.
    const reset = () => {
        if (activeView) forgetWorkingColumns(activeView.id);
        navigate({
            pathname,
            search: activeView
                ? withViewQuery(search, activeView.id, activeView.state.query)
                : withViewQuery(search, activeBuiltInId, baselineSearch),
        });
    };

    const openSaveDialog = (titleId: string) =>
        setDialog({
            mode: 'create',
            titleId,
            name: '',
            baseViewKey: activeBuiltInId,
            state: stateOf(currentQuery, currentColumns),
            describesList: true,
        });

    const duplicate = (view: ListView) =>
        setDialog({
            mode: 'create',
            titleId: 'views.duplicate',
            name: `${view.name} 2`,
            baseViewKey: view.baseViewKey,
            state: view.state,
        });

    const remove = (view: ListView) =>
        deleteView({ id: view.id, listKey })
            .unwrap()
            .then(() => {
                setPinned(viewPin(view), false);
                if (view.id === activeView?.id) openBuiltIn(view.baseViewKey);
            })
            .catch(() => undefined);

    const setAsDefault = (target: ListView | string) =>
        void setDefaultView({
            listKey,
            body: typeof target === 'string' ? { builtInViewKey: target } : { listViewId: target.id },
        })
            .unwrap()
            .catch(() => undefined);

    /** Resolves once the dialog's change is stored, so the dialog closes only then. */
    const submitDialog = async (values: ViewDialogValues) => {
        if (!dialog) return;
        const sharedEmployeeIds = values.visibility === 'PEOPLE' ? values.shared.map(({ id }) => id) : [];
        if (dialog.mode === 'create') {
            const created = await createView({
                listKey,
                baseViewKey: dialog.baseViewKey,
                name: values.name,
                state: dialog.state,
                visibility: values.visibility,
                sharedEmployeeIds,
            }).unwrap();
            if (surface) captureListViewSaved(surface);
            if (values.pin) setPinned(viewPin(created), true);
            if (values.openOn) setAsDefault(created);
            navigate(viewLocation(pathOf(created.baseViewKey), created));
        } else {
            const { view } = dialog;
            await updateView({
                id: view.id,
                listKey,
                body: {
                    name: values.name,
                    state: view.state,
                    visibility: values.visibility,
                    sharedEmployeeIds,
                },
            }).unwrap();
            if (isPinned(viewPin(view).key)) setPinned(viewPin({ ...view, name: values.name }), true);
        }
        setDialog(null);
    };

    return {
        listKey,
        builtIns: page.views,
        ownViews: data?.views.filter((view) => view.canEdit) ?? [],
        sharedViews: data?.views.filter((view) => !view.canEdit) ?? [],
        activeView,
        /** The tab the row marks as open. */
        activeId,
        activeBuiltInId,
        isChanged,
        columnStorage,
        dialog,
        closeDialog: () => setDialog(null),
        openBuiltIn,
        openView,
        reset,
        saveView: () => openSaveDialog('views.save-view'),
        rename: (view: ListView) => setDialog({ mode: 'rename', view }),
        share: (view: ListView) => setDialog({ mode: 'share', view }),
        duplicate,
        remove,
        setAsDefault,
        /** The pin of a saved view, which Pin to sidebar stores and a drag onto Pinned carries. */
        viewPin,
        isViewPinned: (view: ListView) => isPinned(`view:${view.id}`),
        togglePinView: (view: ListView) => togglePin(viewPin(view)),
        submitDialog,
    };
}

export type ListViewsModel = NonNullable<ReturnType<typeof useListViews>>;
